import { ImageInfoRecord, StyleInfoRecord, StyleReferenceRecord } from "@/lib/catalog/types"
import { StyleService } from "./style-service"
import { filteredStyleReferencesAtom, StyleDraft } from "./style-state"
import { store } from "../store"
import { getFeatureDistances } from "@/lib/ai/calibration/ai-calibration-feature"
import { logAiInfo } from "@/lib/ai/utils"
import { startMeasurement } from "@/utils/benchmark"
import { Adjustments } from "@/core/types"
import { projectAtom } from "../project/projects-state"
import { photosAtom } from "../photo/photos-state"
import { getUpdatedStylePresetsMap, isAdjustmentsBW } from "./create-style"
import { pushDialog, resetDialogs } from "../dialogs-state"
import { StylePublishLoadingDialog } from "@/components/Dialogs/StylePublishLoadingDialog"
import { DialogProps } from "@/components/ui/Dialog"
import { atom } from "jotai"
import { processingQueue } from "../processing/processing-queue"
import { previewImporter } from "../importing/preview-importing"
import { getImageInfoRecords } from "../processing/image-info"
import { approveReference } from "../processing/mark-reference"
import { AI } from "@/lib/ai/ai"
// eslint-disable-next-line no-restricted-imports -- Phase 1: store-layer i18n
import i18n from "@/i18n"
import { fetchCurrentAdjustmentsFromHistory } from "../store-history"
import PQueue from "p-queue"
import { isImageInfoEqual, getStyleLut } from "@/storage/indexeddb"
import { MainLUTResolver, serializeLookTable } from "@/io/assets/lut/lut-resolver"
import { PNApiOperations } from "@/api/operations"
import { hashFileContent } from "@/utils/hash"
import { Sentry } from "@/telemetry/sentry"
import {
	findLocalPhotoIdForStyleReference,
	getStyleReferenceRemoteImageId,
	toRemoteStyleReferenceImageId,
} from "./style-reference-id"

export interface PublishStyleDraftResult {
	styleInfo: StyleInfoRecord
	references: StyleReferenceRecord[]
}

/**
 * Scan style presets for unique userLutId values, serialize each LookTable,
 * upload as blobs via signed URLs, and return a lutUrls map.
 */
async function uploadStyleLuts(
	presets: Record<string, Partial<Adjustments>>,
	existingLutUrls?: Record<string, string>,
): Promise<Record<string, string> | undefined> {
	const lutIds = new Set<string>()
	for (const adj of Object.values(presets)) {
		if (adj.userLutId) lutIds.add(adj.userLutId)
	}
	if (lutIds.size === 0) return undefined

	const lutUrls: Record<string, string> = {}
	for (const lutId of lutIds) {
		if (existingLutUrls?.[lutId]) {
			lutUrls[lutId] = existingLutUrls[lutId]
			continue
		}

		const lookTable = (await getStyleLut(lutId)) ?? (await MainLUTResolver.get(lutId))
		if (!lookTable) {
			const message = `[publish-style] LUT not found in IndexedDB or catalog, skipping upload`
			console.warn(message, { lutId })
			Sentry.captureMessage(message, { level: "warning", extra: { lutId } })
			continue
		}

		const blob = serializeLookTable(lookTable)
		const contentDigest = await hashFileContent(blob)
		const [uploadUrl] = await PNApiOperations.getSignedUrls([
			{
				contentType: "octet-stream",
				entityType: "style-lut",
				contentDigest,
			},
		])
		if (!uploadUrl) {
			console.warn(`[publish-style] Failed to get upload URL for LUT ${lutId}`)
			continue
		}

		if (uploadUrl.alreadyExists) {
			lutUrls[lutId] = uploadUrl.url
		} else {
			const { url } = await PNApiOperations.uploadContent({
				contentType: "octet-stream",
				data: blob,
				url: uploadUrl.url,
			})
			lutUrls[lutId] = url
		}
	}

	return Object.keys(lutUrls).length > 0 ? lutUrls : undefined
}

const STYLE_REFERENCE_SYNC_CONCURRENCY = 2

const incrementStylePublishProgress = (delta: number) => {
	store.set(stylePublishProgressAtom, (prev) => prev + delta)
}

const createStyleWithProgress = async (
	name: string,
	presets: Record<string, Partial<Adjustments>>,
	progressDelta = 0,
) => {
	const styleResponse = await StyleService.createStyle(name, presets)
	if (progressDelta > 0) {
		incrementStylePublishProgress(progressDelta)
	}
	return styleResponse
}

const deleteCreatedStyleAfterPublishFailure = async (styleId: string, error: unknown) => {
	try {
		await StyleService.deleteStyle(styleId)
	} catch (deleteError) {
		console.warn("Failed to clean up style after publish failure", deleteError)
		Sentry.captureException(deleteError, {
			tags: { source: "delete_created_style_after_publish_failure" },
			extra: { styleId, originalError: error },
		})
	}
}

const uploadStyleReferencesWithProgress = async (
	referencesToUpload: StyleReferenceRecord[],
	options: {
		styleId: string
		projectId?: string
		progressPerReference: number
	},
) => {
	const uploadedReferences: StyleReferenceRecord[] = []
	const uploadQueue = new PQueue({ concurrency: STYLE_REFERENCE_SYNC_CONCURRENCY })

	await Promise.all(
		referencesToUpload.map((reference) =>
			uploadQueue.add(async () => {
				const uploadedReference = await StyleService.uploadReference(
					reference,
					options.styleId,
					options.projectId,
				)
				uploadedReferences.push(uploadedReference)
				incrementStylePublishProgress(options.progressPerReference)
			}),
		),
	)

	return uploadedReferences
}

const resolveStyleCoverImageUrl = async (options: {
	projectId?: string
	references: StyleReferenceRecord[]
	coverImageData?: Blob
	fallbackCoverImageUrl?: string
}) => {
	if (options.coverImageData) {
		return StyleService.uploadStyleReferenceImage(options.coverImageData)
	}

	if (!options.projectId) {
		return options.fallbackCoverImageUrl
	}

	const photos = store.get(photosAtom)
	let coverPhotoId: string | undefined
	for (let index = options.references.length - 1; index >= 0; index--) {
		const reference = options.references[index]
		if (!reference) continue

		coverPhotoId = findLocalPhotoIdForStyleReference(reference, photos)
		if (coverPhotoId) break
	}

	if (!coverPhotoId) {
		return options.fallbackCoverImageUrl
	}

	const coverImageUrl = await StyleService.uploadCoverImage(coverPhotoId, options.projectId)
	return coverImageUrl ?? options.fallbackCoverImageUrl
}

export const publishStyleDraft = async (
	styleDraft: StyleDraft,
): Promise<PublishStyleDraftResult> => {
	//Create a style in backend
	let styleResponse = await createStyleWithProgress(styleDraft.name, styleDraft.presets)
	const styleId = styleResponse.styleId

	//Upload image info as references
	const referencesToUpload = Object.values(styleDraft.referenceImages).map((imageInfo) => ({
		styleId: styleId,
		imageId: toRemoteStyleReferenceImageId(String(imageInfo.imageId)),
		imageInfo: imageInfo,
		afterImageData: styleDraft.imagesData?.[imageInfo.imageId],
		isImplicit: false,
	}))

	// Progress bar segments for each major publish step
	const publishProgressSegments = {
		uploadReferences: referencesToUpload.length,
		coverImage: 3,
		finalize: 3,
	} as const
	const total =
		publishProgressSegments.uploadReferences +
		publishProgressSegments.coverImage +
		publishProgressSegments.finalize

	store.set(stylePublishProgressAtom, 0)
	pushDialog(StylePublishLoadingDialog, {
		title: i18n.t("store:publishStyle.savingAiStyle"),
		description: i18n.t("store:publishStyle.uploadingReferences"),
		styleDraft,
		total,
	})

	try {
		const lutUrls = await uploadStyleLuts(styleDraft.presets)
		const projectId = store.get(projectAtom)?.id
		const uploadedReferences = await uploadStyleReferencesWithProgress(referencesToUpload, {
			styleId,
			projectId,
			progressPerReference: 1,
		})

		const coverImageUrl = await resolveStyleCoverImageUrl({
			projectId,
			references: uploadedReferences,
			coverImageData: styleDraft.imagesData?.["cover"],
		})

		incrementStylePublishProgress(publishProgressSegments.coverImage)

		if (coverImageUrl || lutUrls || styleDraft.visibility !== undefined) {
			styleResponse = await StyleService.updateStyle({
				styleId,
				coverImageUrl,
				lutUrls,
				visibility: styleDraft.visibility,
			})
		}
		store.set(stylePublishProgressAtom, total)

		const styleInfo = StyleService.responseToStyleInfo(
			styleResponse,
			styleDraft.prioritizeImageIds,
			styleDraft.globalBiases,
		)
		return { styleInfo, references: uploadedReferences }
	} catch (error) {
		await deleteCreatedStyleAfterPublishFailure(styleId, error)
		throw error
	}
}

export const publishCurrentStyleAsNewStyle = async (style: StyleInfoRecord, newName: string) => {
	const project = store.get(projectAtom)
	if (!project) {
		throw new Error("No current project found")
	}

	const currentReferences = store.get(filteredStyleReferencesAtom) //All explicit references and implicit references from other projects
	const implicitRefCandidates = getAllKeyPhotosAsReferenceCandidates(style.styleId) //implicit references from current project

	// Progress bar segments for each major publish step
	const publishProgressSegments = {
		computePresets: 3,
		createStyle: 3,
		uploadReferences: implicitRefCandidates.length + currentReferences.length,
		approveImplicitReferences: implicitRefCandidates.length,
		coverImage: 3,
		finalize: 3,
	} as const
	const total =
		publishProgressSegments.approveImplicitReferences +
		publishProgressSegments.computePresets +
		publishProgressSegments.createStyle +
		publishProgressSegments.uploadReferences +
		publishProgressSegments.coverImage +
		publishProgressSegments.finalize

	store.set(stylePublishProgressAtom, 0)
	pushDialog(StylePublishLoadingDialog, {
		title: i18n.t("store:publishStyle.creatingAiStyle"),
		total,
	})

	const implicitReferencesToAdd = await approveImplicitReferences(implicitRefCandidates)
	// Getting all unique references (explicit or implicit) by imageId, currentReferences take precedence
	let allReferences = Array.from(
		new Map(
			[...implicitReferencesToAdd, ...currentReferences].map((ref) => [
				getStyleReferenceRemoteImageId(ref),
				ref,
			]),
		).values(),
	)

	if (allReferences.length === 0) {
		throw new Error("No style references found")
	}

	//Evict references
	allReferences = evictReferencesIfNeeded(allReferences)

	// Update style presets
	const stylePresets = await getUpdatedStylePresetsMap({
		references: allReferences,
		existingPresetsMap: style.presets,
	})

	store.set(stylePublishProgressAtom, (prev) => prev + publishProgressSegments.computePresets)

	//Create a style in backend
	let styleResponse = await createStyleWithProgress(
		newName,
		stylePresets,
		publishProgressSegments.createStyle,
	)
	const styleId = styleResponse.styleId

	try {
		const lutUrls = await uploadStyleLuts(stylePresets, style.lutUrls)

		//Upload image info as references
		const remainedUploadProgress =
			total -
			store.get(stylePublishProgressAtom) -
			publishProgressSegments.coverImage -
			publishProgressSegments.finalize

		const perUpload = remainedUploadProgress / allReferences.length
		const uploadedReferences = await uploadStyleReferencesWithProgress(allReferences, {
			styleId,
			projectId: project.id,
			progressPerReference: perUpload,
		})

		const coverImageUrl = await resolveStyleCoverImageUrl({
			projectId: project.id,
			references: allReferences,
			fallbackCoverImageUrl: style.coverImageUrl,
		})
		incrementStylePublishProgress(publishProgressSegments.coverImage)

		styleResponse = await StyleService.updateStyle({
			styleId,
			coverImageUrl,
			lutUrls,
		})
		store.set(stylePublishProgressAtom, total)

		const styleInfo = StyleService.responseToStyleInfo(styleResponse)
		return { styleInfo, references: uploadedReferences }
	} catch (error) {
		await deleteCreatedStyleAfterPublishFailure(styleId, error)
		throw error
	}
}

export const stylePublishProgressAtom = atom<number>(0)

export const updateCurrentStyleInTheCloud = async (
	currentStyle: StyleInfoRecord,
	dialogProps: Partial<DialogProps>,
) => {
	const project = store.get(projectAtom)
	if (!project) {
		throw new Error("No current project found")
	}
	const styleId = currentStyle.styleId

	const currentReferences = store.get(filteredStyleReferencesAtom) //All explicit references and implicit references from other projects
	const implicitRefCandidates = getAllKeyPhotosAsReferenceCandidates(styleId) //implicit references from current project

	// Progress bar segments for each major publish step
	const publishProgressSegments = {
		approveImplicitReferences: implicitRefCandidates.length,
		uploadReferences: implicitRefCandidates.length + currentReferences.length,
		fetchRemoteReferences: 3,
		computePresets: 3,
		coverImage: 3,
		finalize: 3,
	} as const
	const total =
		publishProgressSegments.approveImplicitReferences +
		publishProgressSegments.fetchRemoteReferences +
		publishProgressSegments.computePresets +
		publishProgressSegments.uploadReferences +
		publishProgressSegments.coverImage +
		publishProgressSegments.finalize

	store.set(stylePublishProgressAtom, 0)
	pushDialog(StylePublishLoadingDialog, {
		title: i18n.t("store:publishStyle.updatingAiStyle"),
		...dialogProps,
		total,
	})

	const implicitReferencesToAdd = await approveImplicitReferences(implicitRefCandidates)

	// Getting all unique references (explicit or implicit) by imageId, currentReferences take precedence
	const allReferences = Array.from(
		new Map(
			[...implicitReferencesToAdd, ...currentReferences].map((ref) => [
				getStyleReferenceRemoteImageId(ref),
				ref,
			]),
		).values(),
	)

	const remoteReferenceResponses = await StyleService.getReferencesForStyle(styleId)
	const remoteReferences = remoteReferenceResponses.map((response) => {
		return StyleService.responseToReference(response)
	})

	// If remote references are not in allReferences, they are to be deleted
	const referencesToDelete = remoteReferences.filter((remoteRef) => {
		return !allReferences.find(
			(ref) => getStyleReferenceRemoteImageId(ref) === remoteRef.imageId,
		)
	})

	const referencesToUpload = allReferences.filter((localRef) => {
		const remoteImageId = getStyleReferenceRemoteImageId(localRef)
		const remoteRef = remoteReferences.find((ref) => ref.imageId === remoteImageId)

		if (!remoteRef) return true // new reference to upload
		if (!isImageInfoEqual(remoteRef.imageInfo, localRef.imageInfo)) return true // image info changed
		if (Boolean(remoteRef.isImplicit) !== Boolean(localRef.isImplicit)) return true // implicit flag changed
	})

	store.set(
		stylePublishProgressAtom,
		(prev) => prev + publishProgressSegments.fetchRemoteReferences,
	)

	if (referencesToUpload.length === 0 && referencesToDelete.length === 0) {
		console.log("No new references to upload for style update")
		store.set(stylePublishProgressAtom, total)
		resetDialogs()
		return
	}

	// Because referencesToUpload could be less than original candidates, we need to calculate the remaining progress for smoother progress bar
	const remainedUploadProgress =
		total -
		store.get(stylePublishProgressAtom) -
		publishProgressSegments.computePresets -
		publishProgressSegments.coverImage -
		publishProgressSegments.finalize

	const perUploadOrDelete =
		remainedUploadProgress / (referencesToUpload.length + referencesToDelete.length)

	const uploadQueue = new PQueue({ concurrency: STYLE_REFERENCE_SYNC_CONCURRENCY })
	const uploadedReferences: StyleReferenceRecord[] = []
	await Promise.all(
		referencesToUpload.map((reference) =>
			uploadQueue.add(async () => {
				const uploadedReference = await StyleService.uploadReference(
					reference,
					styleId,
					store.get(projectAtom)?.id,
				)
				uploadedReferences.push(uploadedReference)
				store.set(stylePublishProgressAtom, (prev) => prev + perUploadOrDelete)
			}),
		),
	)

	const deleteQueue = new PQueue({ concurrency: STYLE_REFERENCE_SYNC_CONCURRENCY })
	await Promise.all(
		referencesToDelete.map((reference) =>
			deleteQueue.add(async () => {
				await StyleService.deleteStyleReference(styleId, reference.imageId)
				store.set(stylePublishProgressAtom, (prev) => prev + perUploadOrDelete)
			}),
		),
	)

	const updatedRemoteReferencesResponses = await StyleService.getReferencesForStyle(styleId)
	const updatedRemoteReferences = updatedRemoteReferencesResponses.map((response) => {
		return StyleService.responseToReference(response)
	})

	//Update presets for the style
	const stylePresets = await getUpdatedStylePresetsMap({
		references: referencesToUpload,
		existingPresetsMap: currentStyle.presets,
	})

	store.set(stylePublishProgressAtom, (prev) => prev + publishProgressSegments.computePresets)

	const lutUrls = await uploadStyleLuts(stylePresets, currentStyle.lutUrls)
	const coverImageUrl = currentStyle.coverImageUrl

	const updatedStyleResponse = await StyleService.updateStyle({
		styleId,
		coverImageUrl,
		presets: stylePresets,
		lutUrls: lutUrls,
		version: currentStyle.version + 1, //increment version when presets and references are updated
	})

	store.set(stylePublishProgressAtom, total)
	resetDialogs()
	return {
		styleInfo: StyleService.responseToStyleInfo(updatedStyleResponse),
		references: updatedRemoteReferences,
	}
}

function evictReferencesIfNeeded(references: StyleReferenceRecord[], maxRefCount = 2500) {
	const n = references.length
	if (n <= maxRefCount) return references

	const endMeasurement = startMeasurement("evictReferencesIfNeeded")

	const targetCount = Math.max(Math.floor(maxRefCount * 0.8), maxRefCount - 400)

	console.log("evictReferencesIfNeeded", { n, maxRefCount, targetCount })

	// Sort by timestamp, latest first
	const sortedReferences = references.sort(
		(a, b) =>
			(b.imageInfo.features.meta.timestamp ?? 0) - (a.imageInfo.features.meta.timestamp ?? 0),
	)

	// First pass: compute distance and remove very similar ref images using small threshold
	const thresholdSmall = 0.1
	let keptImageIndices: number[] = []
	const dist = new Array(n)
	for (let i = 0; i < n; i++) {
		dist[i] = new Array(n).fill(0)
		const fi = sortedReferences[i]
		if (!fi) continue
		let keepI = true
		for (const j of keptImageIndices) {
			const fj = sortedReferences[j]
			if (!fj) continue
			const d = getFeatureDistances(fi.imageInfo, fj.imageInfo, false, false) // scene, light, wb, meta, overall
			dist[i][j] = dist[j][i] = d[4]
			if (d[4] < thresholdSmall) {
				// similar ref exists
				keepI = false
				break
			}
		}
		if (keepI) keptImageIndices.push(i)
	}

	logAiInfo("first pass", { keptImageIndices, dist })

	// Second passes if needed: use incrementally larger threshold
	// Should be faster than do clustering with n_clusters / threshold
	const dThresh = 0.05
	let threshold = thresholdSmall + dThresh
	const thresholdMax = 0.3
	while (keptImageIndices.length > targetCount && threshold <= thresholdMax) {
		const imageIndices = [...keptImageIndices]
		keptImageIndices = []
		for (const i of imageIndices) {
			const fi = sortedReferences[i]
			if (!fi) continue
			let keepI = true
			for (const j of keptImageIndices) {
				const fj = sortedReferences[j]
				if (!fj) continue

				let distance = dist[i][j]
				if (distance == undefined) {
					// shouldn't happen
					console.warn("dist missing:", i, j)
					const d = getFeatureDistances(fi.imageInfo, fj.imageInfo, false, false)
					dist[i][j] = dist[j][i] = distance = d[4]
				}
				if (distance < threshold) {
					// similar ref exists
					keepI = false
					break
				}
			}
			if (keepI) keptImageIndices.push(i)
		}
		logAiInfo("more pass:", { threshold, keptImageIndices })

		threshold += dThresh
	}

	// Third pass: just remove older refs
	if (keptImageIndices.length > targetCount) {
		keptImageIndices = keptImageIndices.slice(0, targetCount)
	}

	// Update style record with kept images
	const keptReferences: StyleReferenceRecord[] = []
	for (const i of keptImageIndices) {
		const image = sortedReferences[i]
		if (image) keptReferences.push(image)
	}
	logAiInfo({ keptReferences: keptReferences })

	endMeasurement()

	return keptReferences
}

function getAllKeyPhotosAsReferenceCandidates(styleId: string): StyleReferenceRecord[] {
	const imageInfos = getImageInfoRecords()
	const allInfos = Array.from(imageInfos.values())
	return allInfos
		.filter((info: ImageInfoRecord) => {
			return (
				info.keyNeighborId === info.imageId &&
				info.aiStyleAdjustments &&
				!info.needApplyStyle // only keep up-to-date AI-processed key photos
			)
		})
		.map((info) => {
			return {
				styleId: styleId,
				imageId: toRemoteStyleReferenceImageId(String(info.imageId)),
				imageInfo: info,
				isImplicit: true,
			}
		})
}

async function approveImplicitReferences(
	implicitReferences: StyleReferenceRecord[],
): Promise<StyleReferenceRecord[]> {
	if (implicitReferences.length === 0) {
		return []
	}

	const implicitReferencesToAdd: StyleReferenceRecord[] = []
	await processingQueue.pauseQueue()
	await previewImporter.pause()

	try {
		const photos = store.get(photosAtom)
		for (const reference of implicitReferences) {
			const photo = photos[reference.imageInfo.imageId]
			if (
				!photo ||
				photo.isHidden ||
				photo.isUnsupported ||
				photo.adjustmentsDigest == null
			) {
				store.set(stylePublishProgressAtom, (prev) => prev + 1)
				continue
			}

			// Skip if the photo is locked and the adjustments are BW
			if (photo.kind === "locked") {
				const currentAdjustments = await fetchCurrentAdjustmentsFromHistory(photo)
				if (isAdjustmentsBW(currentAdjustments)) {
					store.set(stylePublishProgressAtom, (prev) => prev + 1)
					continue
				}
			}

			try {
				await AI.lockEditor()
				reference.imageInfo = await approveReference(photo, false)
				implicitReferencesToAdd.push(reference)
			} finally {
				void AI.unlockEditor()
				store.set(stylePublishProgressAtom, (prev) => prev + 1)
			}
		}
	} finally {
		void processingQueue.resumeQueue()
		void previewImporter.resumeIfNeeded()
	}

	console.log("Added implicit references count:", implicitReferencesToAdd.length)
	return implicitReferencesToAdd
}
